/* somada-studies.js — progressive enhancement for Somada Studies articles.
 *
 * STATUS: WIRED 2026-09-05 from sections/main-article.liquid (gated on is_fragment,
 * theme v1.34). Its styles live in that section's stylesheet under .tldr-pop*.
 *
 * WHY THIS LIVES IN THE THEME AND NOT IN THE ARTICLE
 * --------------------------------------------------
 * The article fragment is stored in Shopify's article body, one copy per
 * article. A script placed there is duplicated into every article forever, and
 * fixing a bug means re-rendering and re-publishing all of them. Here it is one
 * file, shared, with nothing stored per article. This is the whole reason the
 * renderer strips <script> from the fragment and asserts its absence.
 *
 * DESIGN RULES
 * ------------
 * 1. Progressive enhancement only. Every feature below has a working CSS
 *    fallback already shipped in article-shell.html. If this file never loads,
 *    or throws, the article stays readable and nothing looks broken.
 * 2. Never move content that search engines or screen readers need in place.
 *    The TL;DR is MOVED, not copied, so the text exists exactly once.
 * 3. Touch nothing outside .somada-article.
 */
(function () {
  'use strict';

  var root = document.querySelector('.somada-article');
  if (!root) return;

  var MOBILE = '(max-width: 1040px)';

  /* ── 1 · TL;DR as a popup at the top, on phones ──────────────────────────
   * On desktop the TL;DR sits in the right-hand rail. On a phone the rail
   * collapses to the BOTTOM of the article, which is the one place a summary
   * is useless. This moves it above the short answer and puts it behind a
   * button, so it opens on tap and closes on the cross or on an outside tap.
   * It is a move, not a copy: the text appears once in the DOM either way.
   */
  function tldrPopup() {
    var tldr = root.querySelector('.tldr');
    var verdict = root.querySelector('.verdict');
    if (!tldr || !verdict || tldr.dataset.somadaMoved) return;

    var home = document.createComment('tldr-home');
    tldr.parentNode.insertBefore(home, tldr);

    var wrap = document.createElement('div');
    wrap.className = 'tldr-pop';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tldr-pop__btn';
    btn.setAttribute('aria-expanded', 'false');
    var label = tldr.querySelector('.tldr__label');
    btn.textContent = label ? label.textContent : 'TL;DR';

    // One-line invitation above the button (Athan, 2026-09-05 mobile review).
    var lead = document.createElement('p');
    lead.className = 'tldr-pop__lead';
    lead.textContent = 'For the brains, here is a quick summary in scientific language.';

    var panel = document.createElement('div');
    panel.className = 'tldr-pop__panel';
    panel.hidden = true;

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'tldr-pop__x';
    close.setAttribute('aria-label', 'Close summary');
    close.innerHTML = '&times;';

    function open(state) {
      panel.hidden = !state;
      btn.setAttribute('aria-expanded', String(state));
    }

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      open(panel.hidden);
    });
    close.addEventListener('click', function (e) {
      e.stopPropagation();
      open(false);
    });
    panel.addEventListener('click', function (e) { e.stopPropagation(); });
    document.addEventListener('click', function () { open(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') open(false);
    });

    panel.appendChild(close);
    panel.appendChild(tldr);
    wrap.appendChild(lead);
    wrap.appendChild(btn);
    wrap.appendChild(panel);
    verdict.parentNode.insertBefore(wrap, verdict);

    tldr.dataset.somadaMoved = '1';
    tldr.dataset.somadaHome = '1';
    root._somadaTldrHome = home;
    root._somadaTldrWrap = wrap;
  }

  function tldrRestore() {
    var tldr = root.querySelector('.tldr');
    var home = root._somadaTldrHome;
    var wrap = root._somadaTldrWrap;
    if (!tldr || !home || !wrap || !tldr.dataset.somadaMoved) return;
    home.parentNode.insertBefore(tldr, home);
    wrap.parentNode.removeChild(wrap);
    delete tldr.dataset.somadaMoved;
    root._somadaTldrWrap = null;
  }

  /* ── 2 · citation tooltips get a real close ──────────────────────────────
   * The CSS version opens on :focus-within and closes on blur, which works but
   * gives the cross nothing to actually do. With script the cross is a real
   * control and the panel can be dismissed deliberately.
   */
  function tooltips() {
    root.addEventListener('click', function (e) {
      var tip = e.target.closest ? e.target.closest('.cite__tip') : null;
      if (tip) { e.preventDefault(); }
      var cite = e.target.closest ? e.target.closest('.cite') : null;
      root.querySelectorAll('.cite.is-open').forEach(function (c) {
        if (c !== cite) c.classList.remove('is-open');
      });
      if (cite && !tip) {
        e.preventDefault();
        cite.classList.toggle('is-open');
      } else if (tip) {
        var owner = tip.closest('.cite');
        if (owner) owner.classList.remove('is-open');
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      root.querySelectorAll('.cite.is-open').forEach(function (c) {
        c.classList.remove('is-open');
      });
    });
  }

  /* ── 3 · breakpoint wiring ───────────────────────────────────────────── */
  var mq = window.matchMedia(MOBILE);
  function sync() { if (mq.matches) tldrPopup(); else tldrRestore(); }

  try {
    sync();
    tooltips();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
  } catch (err) {
    /* Enhancement only. A failure here must never take the article with it. */
    if (window.console && console.warn) console.warn('somada-studies:', err);
  }
})();
